import { useEffect, useMemo, useState } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";

import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import {
  THEMES,
  WEEKLY_CHALLENGES,
  MONTHLY_CHALLENGES,
  CREDIT_STORE,
  getUnlockedThemes,
  setUserTheme,
  spendCredits,
  applyTheme,
} from "../services/userPerksService";
import PerksInventory from "../components/PerksInventory";
import PerksCreditsHub, { ReferralSpendCard, ThemeCatalogSpendCard } from "../components/perks/PerksCreditsHub";
import { PipInteractive } from "../components/Pip";
import PipAnchor from "../components/Pip/PipAnchor";
import PipPageTip from "../components/Pip/PipPageTip";
import { checkPipCooldown } from "../services/pipService";
import { formatCount } from "../utils/formatCount";
import { hasCreatorAccess } from "../utils/roleUtils";
import "./Perks.css";

const STROKE = 1.8;

const WALL_NOTE_ITEM = {
  id: "wall_note",
  name: "Send a Wall Note",
  description:
    "Write a personal note (250–500 chars) to a creator. They choose to pin it on their Appreciation Wall.",
  cost: 1500,
  category: "wall",
};

const SECRET_CHALLENGES = [
  {
    title: "Unknown challenge",
    body: "Keep engaging. Something will unlock when the time is right.",
    hint: "keep going — you're closer than you think",
  },
  {
    title: "Unknown challenge",
    body: "Some things are earned, not tracked. You'll know when it happens.",
    hint: "not everything has a progress bar",
  },
];

const THEME_CARDS = [
  {
    id: "warmCream",
    name: "Warm Cream",
    description: "The platform default. Warm, editorial, easy on the eyes.",
    status: "free",
    preview: {
      bg: "#F2EFE9",
      sidebar: "#EDE9E2",
      accent: "#C4790A",
      muted: "#9A9088",
      text: "#0E0C0A",
      cardBg: "#fff",
      border: "#DDD8CE",
    },
  },
  {
    id: "cleanWhite",
    name: "Clean White",
    description: "Crisp and minimal. A clean slate for your content.",
    status: "free",
    preview: {
      bg: "#FAFAFA",
      sidebar: "#F0F0F0",
      accent: "#C4790A",
      muted: "#999999",
      text: "#111111",
      cardBg: "#fff",
      border: "#E8E8E8",
    },
  },
  {
    id: "midnight",
    name: "Midnight",
    description: "Sleek dark mode. For late night drops and serious fans.",
    status: "soon",
    preview: {
      bg: "#0A0A0A",
      sidebar: "#141414",
      accent: "#C4790A",
      muted: "#555555",
      dark: true,
    },
  },
  {
    id: "forest",
    name: "Forest",
    description: "Deep earthy greens. Canadian wilderness energy.",
    status: "soon",
    preview: {
      bg: "#0A1209",
      sidebar: "#0F1A12",
      accent: "#1A8C52",
      muted: "#4ade80",
      dark: true,
    },
  },
  {
    id: "goldenHour",
    name: "Golden Hour",
    description: "Warm amber and burnt gold. The magic hour, bottled.",
    status: "soon",
    preview: {
      bg: "#120e06",
      sidebar: "#1a1409",
      accent: "#C4790A",
      muted: "#f59e0b",
      dark: true,
    },
  },
  {
    id: "dusk",
    name: "Soft Dusk",
    description: "Muted lavender and warm grey. The sky 20 minutes after sunset.",
    status: "soon",
    preview: {
      bg: "#1A1624",
      sidebar: "#221E2E",
      accent: "#C4B5D4",
      muted: "#C4B5D4",
      dark: true,
    },
  },
  {
    id: "ember",
    name: "Ember",
    description: "Deep terracotta and rust. Campfire warmth, pottery studio calm.",
    status: "soon",
    preview: {
      bg: "#1A0A06",
      sidebar: "#240E08",
      accent: "#C4603A",
      muted: "#C4603A",
      dark: true,
    },
  },
  {
    id: "northern",
    name: "Northern",
    description: "Cool slate and white. Clean Canadian winter energy.",
    status: "soon",
    preview: {
      bg: "#0A0E14",
      sidebar: "#0E141C",
      accent: "#8AAEC4",
      muted: "#8AAEC4",
      dark: true,
    },
  },
  {
    id: "obsidian",
    name: "Obsidian",
    description: "Pure black with silver accents. Sharp, editorial, timeless.",
    status: "soon",
    preview: {
      bg: "#050505",
      sidebar: "#0A0A0A",
      accent: "#C0C0C0",
      muted: "#C0C0C0",
      dark: true,
    },
  },
  {
    id: "aurora",
    name: "Aurora",
    description: "Northern lights across your profile. Exclusive to founding members.",
    status: "founder",
    serviceId: "aurora",
    preview: {
      bg: "#08040f",
      sidebar: "#130820",
      accent: "#C4790A",
      muted: "#C4790A",
      dark: true,
      gradient: true,
    },
  },
  {
    id: "maple",
    name: "Maple",
    description: "Deep Canadian red and cream. For the OGs who were here from day one.",
    status: "founder",
    serviceId: "noir",
    preview: {
      bg: "#0E0404",
      sidebar: "#180808",
      accent: "#C41E3A",
      muted: "#C41E3A",
      dark: true,
    },
  },
];

const CHALLENGE_STYLES = {
  first_viewer: { bg: "#FDF3E0", stroke: "#C4790A" },
  conversation_starter: { bg: "#F0F0F0", stroke: "#3C3830" },
  new_discovery: { bg: "#E4F5EE", stroke: "#1A8C52" },
  drop_supporter: { bg: "#FFF0E6", stroke: "#C4790A" },
  spread_word: { bg: "#F0F8F0", stroke: "#1A8C52" },
  superfan: { bg: "#FDF3E0", stroke: "#C4790A" },
  early_adopter: { bg: "#FFF0E6", stroke: "#C4790A" },
  community_builder: { bg: "#E4F5EE", stroke: "#1A8C52" },
};

const STORE_STYLES = {
  boost_creator: { bg: "#FDF3E0", stroke: "#C4790A" },
  drop_alert: { bg: "#FFF0E6", stroke: "#C4790A" },
  super_comment: { bg: "#F0F0F0", stroke: "#9A9088" },
  wall_note: { bg: "#E4F5EE", stroke: "#1A8C52" },
  credit_tip_small: { bg: "#FDF3E0", stroke: "#C4790A" },
  credit_tip_large: { bg: "#F0F0F0", stroke: "#9A9088" },
};

const STORE_LABELS = {
  boost_creator: "Boost Creator",
  drop_alert: "Drop Alert",
  super_comment: "Super Comment",
  credit_tip_small: "Credit Tip — Small",
  credit_tip_large: "Credit Tip — Large",
};

/* ── SVG icons (1.8px stroke) ── */
function IconCheck({ size = 12, color = "white", sw = 3 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function IconBolt({ size = 18, color = "#C4790A", sw = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  );
}

function IconStar({ size = 14, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function IconPalette({ size = 14, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
    </svg>
  );
}

function IconStore({ size = 14, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}

function IconBag({ size = 14, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}

function IconLock({ size = 18, color = "#9A9088" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function IconChevron({ size = 16, color = "#9A9088" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function IconInfo({ size = 20, color = "#C4790A" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function IconHeart({ size = 13, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function IconMessage({ size = 13, color = "#C4790A" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconClock({ size = 13, color = "#C4790A" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4l3 3" />
    </svg>
  );
}

function IconArrow({ size = 13, color = "#C4790A" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

function IconCrown({ size = 9, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
      <path d="M2 20h20M4 20V8l4 3 4-6 4 6 4-3v12" />
    </svg>
  );
}

function ChallengeIcon({ id, size = 20, variant = "weekly" }) {
  const style = CHALLENGE_STYLES[id] || { bg: "#FDF3E0", stroke: "#C4790A" };
  const wrapClass = variant === "monthly" ? "pk-mo-icon" : "pk-ch-icon-wrap";
  const icons = {
    first_viewer: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={style.stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
    conversation_starter: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={style.stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
    new_discovery: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={style.stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35" />
      </svg>
    ),
    drop_supporter: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={style.stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
    spread_word: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={style.stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
      </svg>
    ),
    superfan: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={style.stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
    early_adopter: <IconBolt size={size} color={style.stroke} />,
    community_builder: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={style.stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  };
  return (
    <div className={wrapClass} style={{ background: style.bg }}>
      {icons[id] || icons.new_discovery}
    </div>
  );
}

function StoreIcon({ id, size = 22 }) {
  const style = STORE_STYLES[id] || { bg: "#FDF3E0", stroke: "#C4790A" };
  const icons = {
    boost_creator: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={style.stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <path d="M5 12h14M12 5l7 7-7 7" />
      </svg>
    ),
    drop_alert: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={style.stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
    super_comment: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={style.stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
    wall_note: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={style.stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    ),
    credit_tip_small: <IconHeart size={size} color={style.stroke} />,
    credit_tip_large: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={style.stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
  };
  return (
    <div className="pk-st-icon" style={{ background: style.bg }}>
      {icons[id] || icons.boost_creator}
    </div>
  );
}

function ThemePreview({ card, isActive, locked }) {
  const p = card.preview;
  const dark = p.dark;
  const showOverlay = card.status === "soon" || (card.status === "founder" && locked);
  return (
    <div className="pk-th-preview" style={{ background: p.bg }}>
      <div className="pk-th-sidebar" style={{ background: p.sidebar }}>
        <div className="pk-th-dot" style={{ background: p.accent }} />
        <div className="pk-th-dot pk-th-dot--sm" style={{ background: p.muted }} />
        <div className="pk-th-dot pk-th-dot--sm" style={{ background: p.muted }} />
      </div>
      <div className="pk-th-feed">
        <div
          className="pk-th-mini-card"
          style={{
            border: dark ? "1px solid rgba(255,255,255,0.08)" : `1px solid ${p.border}`,
            background: dark ? "rgba(255,255,255,0.04)" : p.cardBg,
          }}
        >
          <div
            className="pk-th-mini-img"
            style={{
              background: p.gradient
                ? "linear-gradient(135deg,rgba(196,121,10,0.2),rgba(196,121,10,0.1))"
                : dark
                  ? "rgba(255,255,255,0.08)"
                  : p.sidebar,
            }}
          />
          <div className="pk-th-mini-bar" style={{ background: p.accent }} />
          <div className="pk-th-mini-lines">
            <div
              className="pk-th-line"
              style={{
                background: dark ? "rgba(255,255,255,0.6)" : p.text,
                width: "70%",
              }}
            />
            <div
              className="pk-th-line"
              style={{
                background: dark ? "rgba(255,255,255,0.3)" : p.muted,
                width: "45%",
              }}
            />
          </div>
        </div>
      </div>
      {isActive && <div className="pk-th-active-tag">✓ Active</div>}
      {showOverlay && (
        <div className="pk-th-overlay">
          <div className="pk-th-overlay-pill">
            {card.status === "founder" ? "Founding Members Only" : "Coming Soon"}
          </div>
        </div>
      )}
    </div>
  );
}

function progressLabel(progress, target, completed) {
  if (completed) return "Completed";
  if (progress === 0) return "Not started";
  if (progress >= target * 0.8 && progress < target) return `${progress} of ${target} — so close!`;
  return "In progress";
}

function dailyStreakCredits(streakVal) {
  if (streakVal >= 30) return 25;
  if (streakVal >= 14) return 15;
  if (streakVal >= 7) return 10;
  return 5;
}

export default function Perks() {
  const { currentUser, userPerks, refreshUserPerks, refreshUserProfile, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState("challenges");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [pipHiding, setPipHiding] = useState(false);

  const perks = userPerks || {};
  const credits = perks.credits || 0;
  const streak = perks.streak || 0;
  const points = perks.points || 0;
  const weeklyProgress = perks.weeklyProgress || {};
  const weeklyCompleted = perks.weeklyCompleted || [];
  const monthlyProgress = perks.monthlyProgress || {};
  const monthlyCompleted = perks.monthlyCompleted || [];
  const unlockedThemes = getUnlockedThemes(perks);
  const activeTheme = userProfile?.theme || perks.theme || "warmCream";
  const isFounding = perks.isFoundingMember || perks.isFounder;

  const firstName =
    userProfile?.displayName?.split(" ")[0] ||
    currentUser?.displayName?.split(" ")[0] ||
    "there";

  useEffect(() => {
    const sync = () => setPipHiding(checkPipCooldown(userPerks).onCooldown);
    sync();
    const interval = setInterval(sync, 60_000);
    return () => clearInterval(interval);
  }, [userPerks]);

  // Re-fetch Auth balance on every Perks visit so hero matches Credits hub / Profile
  useEffect(() => {
    if (currentUser?.uid) refreshUserPerks(currentUser.uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  useEffect(() => {
    const loadPurchases = async () => {
      if (!currentUser) return;
      try {
        const snap = await getDocs(
          query(collection(db, "creditTransactions"), where("userId", "==", currentUser.uid))
        );
        setPurchases(
          snap.docs
            .map((d) => ({
              id: d.id,
              ...d.data(),
              itemId: d.data().type,
              purchasedAt: d.data().createdAt,
            }))
            .filter((d) => d.amount < 0)
        );
      } catch (err) {
        console.error("Purchases load error:", err);
      }
    };
    loadPurchases();
  }, [currentUser]);

  const refreshPurchases = async () => {
    if (!currentUser) return;
    try {
      const snap = await getDocs(
        query(collection(db, "creditTransactions"), where("userId", "==", currentUser.uid))
      );
      setPurchases(
        snap.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
            itemId: d.data().type,
            purchasedAt: d.data().createdAt,
          }))
          .filter((d) => d.amount < 0)
      );
    } catch (err) {
      console.error("Purchases refresh error:", err);
    }
  };

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleThemeSelect = async (themeId) => {
    if (!currentUser) return;
    if (!unlockedThemes.includes(themeId)) {
      showMessage("This theme is locked.", "error");
      return;
    }
    setLoading(true);
    try {
      applyTheme(themeId);
      await setUserTheme(currentUser.uid, themeId);
      await Promise.all([
        refreshUserPerks(currentUser.uid),
        refreshUserProfile(currentUser.uid),
      ]);
      showMessage(`${THEMES[themeId]?.name || themeId} theme applied!`);
    } catch (error) {
      console.error("Theme apply error:", error);
      showMessage("Could not apply theme.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleThemeCardClick = (card) => {
    if (card.status === "soon") {
      showMessage("This theme is coming soon!", "error");
      return;
    }
    if (card.status === "founder" && card.locked) {
      showMessage("Founding members only.", "error");
      return;
    }
    if (card.selectable) {
      handleThemeSelect(card.serviceId);
    }
  };

  const handlePurchase = async (item) => {
    if (!currentUser) return;
    if (credits < item.cost) {
      showMessage(`Need ${formatCount(item.cost - credits)} more credits`, "error");
      return;
    }
    setLoading(true);
    try {
      const result = await spendCredits(currentUser.uid, item.cost, item.id, {
        description: item.name,
        themeId: item.themeId,
      });
      if (result.success) {
        await refreshUserPerks(currentUser.uid);
        await refreshPurchases();
        showMessage(
          item.themeId ? `${item.name} unlocked! Select it in Themes.` : `${item.name} purchased!`
        );
      } else {
        showMessage(result.error || "Purchase failed", "error");
      }
    } catch {
      showMessage("Purchase failed. Try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const todayIndex = new Date().getDay();
  const adjustedToday = todayIndex === 0 ? 6 : todayIndex - 1;
  const dailyEarned = dailyStreakCredits(streak);

  const streakMilestoneDays = streak < 7 ? 7 : streak < 14 ? 14 : streak < 30 ? 30 : null;
  const streakMilestoneReward =
    streak < 7 ? 50 : streak < 14 ? 75 : streak < 30 ? 150 : null;
  const daysToMilestone = streakMilestoneDays ? streakMilestoneDays - streak : 0;

  const pipMessage =
    streakMilestoneDays && daysToMilestone > 0
      ? `${daysToMilestone} day${daysToMilestone === 1 ? "" : "s"} away from your ${streakMilestoneDays}-day bonus. Don't break the streak!`
      : streak >= 7
        ? "You're on a roll — keep the streak alive!"
        : "Earn credits and I might hide somewhere for you to find.";

  const creatorStoreItems = useMemo(
    () =>
      CREDIT_STORE.filter(
        (i) =>
          i.category === "creator" &&
          !["credit_tip_small", "credit_tip_large"].includes(i.id)
      ),
    []
  );

  const wallStoreItems = useMemo(() => {
    const tips = CREDIT_STORE.filter((i) =>
      ["credit_tip_small", "credit_tip_large"].includes(i.id)
    );
    return [WALL_NOTE_ITEM, ...tips];
  }, []);

  const affordItems = useMemo(() => {
    const items = [];
    if (credits >= WALL_NOTE_ITEM.cost) items.push("Appreciation Wall note");
    const boost = creatorStoreItems.find((i) => i.id === "boost_creator");
    if (boost && credits >= boost.cost) items.push("Creator boost");
    if (credits >= 500) items.push("1 app look unlock");
    return items;
  }, [credits, creatorStoreItems]);

  const valueBar = useMemo(() => {
    const wallCost = WALL_NOTE_ITEM.cost;
    const boostCost = creatorStoreItems.find((i) => i.id === "boost_creator")?.cost || 100;
    let remaining = credits;
    const parts = [];
    if (remaining >= wallCost) {
      parts.push({ label: "1× Appreciation Wall note", highlight: "1×" });
      remaining -= wallCost;
    }
    if (remaining >= 500) {
      parts.push({ label: "1 app look unlock", highlight: "+" });
      remaining -= 500;
    }
    if (remaining >= boostCost) {
      parts.push({ label: "1 creator boost", highlight: "+" });
      remaining -= boostCost;
    }
    return { parts, leftover: remaining };
  }, [credits, creatorStoreItems]);

  const themeCards = useMemo(
    () =>
      THEME_CARDS.map((card) => {
        const serviceId = card.serviceId || card.id;
        const selectable =
          card.status === "free" ||
          (card.status === "founder" && isFounding && unlockedThemes.includes(serviceId));
        const locked = !selectable;
        return {
          ...card,
          serviceId,
          locked,
          selectable,
          isActive: activeTheme === serviceId,
        };
      }),
    [activeTheme, isFounding, unlockedThemes]
  );

  const tabs = [
    { id: "challenges", label: "Challenges", Icon: IconStar },
    { id: "appLook", label: "App Look", Icon: IconPalette },
    { id: "store", label: "Credit Store", Icon: IconStore },
    { id: "owned", label: "My Items", Icon: IconBag },
  ];

  return (
    <div className="pk-page">
      {message && (
        <div className={`pk-toast pk-toast--${message.type === "error" ? "error" : "success"}`}>
          {message.type === "error" ? (
            <IconBolt size={14} color="white" />
          ) : (
            <IconCheck size={14} />
          )}
          {message.text}
        </div>
      )}

      {/* HERO — always dark */}
      <section className="pk-hero">
        <div className="pk-hero-dots" />
        <div className="pk-hero-inner">
          <div className="pk-hero-left">
            <div className="pk-hero-eyebrow">Your perks balance</div>
            <PipAnchor id="onboard-credits" style={{ display: "block" }}>
              <div className="pk-hero-balance">{formatCount(credits)}</div>
            </PipAnchor>
            <PipPageTip
              anchorId="onboard-credits"
              flag="seenCreditsTip"
              text="These are your credits — spend them on unlocks, themes, and Perks."
            />
            <div className="pk-hero-balance-sub">credits available to spend</div>
            <div className="pk-hero-stats">
              <div className="pk-hero-stat">
                <strong>{streak}</strong>
                <span>day streak</span>
              </div>
              <div className="pk-hero-divider" />
              <div className="pk-hero-stat">
                <strong>{formatCount(points)}</strong>
                <span>total points</span>
              </div>
              <div className="pk-hero-divider" />
              <div className="pk-hero-stat">
                <strong>{perks.totalChallengesCompleted || 0}</strong>
                <span>challenges done</span>
              </div>
            </div>
          </div>

          <div className="pk-hero-pip">
            {pipHiding ? (
              <div className="pk-pip-hiding">
                <IconLock size={24} color="rgba(255,255,255,0.4)" />
                <p>Pip is hiding somewhere on the site...</p>
                <small>Find him for +50 credits</small>
              </div>
            ) : (
              <>
                <PipInteractive size={90} outfit={userPerks?.pipOutfit} />
                <div className="pk-pip-bubble">
                  <strong>Hey {firstName}!</strong>
                  {pipMessage}
                  <small style={{ display: "block", marginTop: 6, opacity: 0.8 }}>Click Pip for a surprise — no credits, just fun.</small>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* STREAK BAR */}
      <section className="pk-streak-bar">
        <div className="pk-streak-inner">
          <div className="pk-streak-icon-wrap">
            <IconBolt size={18} />
          </div>
          <div className="pk-streak-text">
            <strong>
              {streak} day streak — +{dailyEarned} credits earned today
            </strong>
            <p>
              {streakMilestoneDays && daysToMilestone > 0
                ? `${daysToMilestone} more day${daysToMilestone === 1 ? "" : "s"} unlocks your ${streakMilestoneDays}-day milestone bonus (+${streakMilestoneReward} credits)`
                : streak >= 30
                  ? "Legendary streak — keep showing up!"
                  : "Visit daily to grow your streak and earn bonus credits."}
            </p>
          </div>
          <div className="pk-streak-days">
            {days.map((day, i) => {
              const isDone = i < adjustedToday || (i === adjustedToday && streak > 0);
              const isToday = i === adjustedToday;
              return (
                <div key={day} className="pk-sday">
                  <div
                    className={`pk-sdot ${
                      isDone && !isToday
                        ? "pk-sdot--done"
                        : isToday
                          ? "pk-sdot--today"
                          : "pk-sdot--pending"
                    }`}
                  >
                    {isDone && !isToday ? (
                      <IconCheck size={12} />
                    ) : isToday ? (
                      <IconBolt size={12} sw={2.5} />
                    ) : null}
                  </div>
                  <span>{day}</span>
                </div>
              );
            })}
          </div>
          {streakMilestoneDays && daysToMilestone > 0 && (
            <div className="pk-streak-cta">
              <IconBolt size={11} sw={2.5} />
              +{streakMilestoneReward} credits in {daysToMilestone} day
              {daysToMilestone === 1 ? "" : "s"}
            </div>
          )}
        </div>
      </section>

      <div className="pk-inner">
        {/* VALUE BAR */}
        <div className="pk-value-bar">
          <div className="pk-value-label">{formatCount(credits)} credits =</div>
          <div className="pk-value-items">
            {valueBar.parts.map((part) => (
              <div key={part.label} className="pk-value-item">
                {part.label.includes("Wall") ? (
                  <IconMessage size={13} />
                ) : part.label.includes("app look") ? (
                  <IconClock size={13} />
                ) : (
                  <IconArrow size={13} />
                )}
                <span className="pk-value-item-gold">{part.highlight}</span>
                {part.label.replace(/^[+1×]+\s*/, "")}
              </div>
            ))}
            {valueBar.leftover > 0 && (
              <div className="pk-value-item pk-value-leftover">
                + {formatCount(valueBar.leftover)} left over
              </div>
            )}
          </div>
        </div>

        {/* TABS */}
        <div className="pk-tabs" role="tablist">
          {tabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={activeTab === id}
              data-tab={id}
              className={`pk-tab${activeTab === id ? " pk-tab--active" : ""}`}
              onClick={() => setActiveTab(id)}
            >
              <Icon size={14} color={activeTab === id ? "white" : "currentColor"} />
              {label}
              {id === "owned" && purchases.length > 0 && (
                <span className="pk-tab-count">{purchases.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* CHALLENGES */}
        {activeTab === "challenges" && (
          <div role="tabpanel">
            <div className="pk-sec-row">
              <div>
                <div className="pk-sec-eyebrow">This week</div>
                <h2 className="pk-sec-title">
                  Weekly <em>challenges</em>
                </h2>
              </div>
              <div className="pk-reset-pill">
                <IconBolt size={11} sw={2.5} />
                Resets Monday
              </div>
            </div>

            <div className="pk-ch-grid">
              {WEEKLY_CHALLENGES.map((challenge) => {
                const progress = weeklyProgress[challenge.id] || 0;
                const completed = weeklyCompleted.includes(challenge.id);
                const pct = Math.round((progress / challenge.target) * 100);
                const almost = !completed && pct >= 80;
                return (
                  <article
                    key={challenge.id}
                    className={`pk-ch-card${completed ? " pk-ch-card--complete" : almost ? " pk-ch-card--almost" : ""}`}
                  >
                    {completed && (
                      <div className="pk-ch-badge pk-ch-badge--done">
                        <IconCheck size={10} />
                        Done
                      </div>
                    )}
                    {almost && (
                      <div className="pk-ch-badge pk-ch-badge--almost">
                        <IconBolt size={10} color="white" sw={2.5} />
                        Almost!
                      </div>
                    )}
                    <div className="pk-ch-top">
                      <ChallengeIcon id={challenge.id} />
                      <div className={`pk-ch-reward${completed ? " pk-ch-reward--done" : ""}`}>
                        +{challenge.reward} <small>credits</small>
                      </div>
                    </div>
                    <h3>{challenge.name}</h3>
                    <p>{challenge.description}</p>
                    <div className="pk-prog">
                      <div
                        className="pk-prog-fill"
                        style={{
                          width: `${completed ? 100 : pct}%`,
                          background: completed ? "var(--pk-green)" : "var(--pk-gold)",
                        }}
                      />
                    </div>
                    <div className="pk-prog-meta">
                      <span>{progressLabel(progress, challenge.target, completed)}</span>
                      <span>
                        {Math.min(progress, challenge.target)}/{challenge.target}
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="pk-sec-row">
              <div>
                <div className="pk-sec-eyebrow">This month</div>
                <h2 className="pk-sec-title">
                  Monthly <em>challenges</em>
                </h2>
              </div>
            </div>

            <div className="pk-mo-grid">
              {MONTHLY_CHALLENGES.map((challenge) => {
                const progress = monthlyProgress[challenge.id] || 0;
                const completed = monthlyCompleted.includes(challenge.id);
                const pct = Math.round((progress / challenge.target) * 100);
                return (
                  <article
                    key={challenge.id}
                    className={`pk-mo-card${completed ? " pk-mo-card--complete" : ""}`}
                  >
                    <ChallengeIcon id={challenge.id} size={24} variant="monthly" />
                    <div className="pk-mo-body">
                      <h3>{challenge.name}</h3>
                      <p>{challenge.description}</p>
                      <div className="pk-prog">
                        <div
                          className="pk-prog-fill"
                          style={{
                            width: `${completed ? 100 : pct}%`,
                            background: "var(--pk-gold)",
                          }}
                        />
                      </div>
                      <div className="pk-prog-meta">
                        <span>
                          {progress} of {challenge.target}
                          {challenge.id === "early_adopter" ? " drops" : ""}
                          {challenge.id === "community_builder" ? " referral" : ""}
                        </span>
                        <span>{completed ? "100" : pct}%</span>
                      </div>
                    </div>
                    <div className="pk-mo-reward">
                      <strong>+{challenge.reward}</strong>
                      <span>credits</span>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="pk-sec-row">
              <div>
                <div className="pk-sec-eyebrow">Hidden</div>
                <h2 className="pk-sec-title">
                  Secret <em>challenges</em>
                </h2>
              </div>
            </div>

            <div className="pk-secret-list">
              {SECRET_CHALLENGES.map((secret) => (
                <div key={secret.hint} className="pk-secret-card">
                  <div className="pk-secret-lock">
                    <IconLock />
                  </div>
                  <div>
                    <h3>{secret.title}</h3>
                    <p>{secret.body}</p>
                    <span className="pk-secret-hint">{secret.hint}</span>
                  </div>
                  <IconChevron />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* APP LOOK */}
        {activeTab === "appLook" && (
          <div role="tabpanel">
            <div className="pk-sec-row">
              <div>
                <div className="pk-sec-eyebrow">Display</div>
                <h2 className="pk-sec-title">
                  Choose your <em>app look</em>
                </h2>
              </div>
            </div>

            <div className="pk-themes-note">
              <IconInfo />
              <span>
                <strong>Your app look follows you everywhere.</strong> Pick the chrome that feels like
                you. Free looks are yours now — unlock more as you grow your credits and streak.
              </span>
            </div>

            <div className="pk-catalog-entry">
              <ReferralSpendCard />
            </div>

            {hasCreatorAccess(userProfile) && (
              <div className="pk-catalog-entry">
                <ThemeCatalogSpendCard />
              </div>
            )}

            <div className="pk-th-grid">
              {themeCards.map((card) => (
                <article
                  key={card.id}
                  className={`pk-th-card${card.isActive ? " pk-th-card--active" : ""}${card.locked ? " pk-th-card--locked" : ""}`}
                  onClick={() => handleThemeCardClick(card)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleThemeCardClick(card);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <ThemePreview card={card} isActive={card.isActive} locked={card.locked} />
                  <div className="pk-th-info">
                    <h3>{card.name}</h3>
                    <p>{card.description}</p>
                    {card.status === "free" && (
                      <div className="pk-th-pill pk-th-pill--free">
                        <IconCheck size={9} color="currentColor" />
                        {card.isActive ? "Free — active" : "Free"}
                      </div>
                    )}
                    {card.status === "soon" && (
                      <div className="pk-th-pill pk-th-pill--soon">Coming soon</div>
                    )}
                    {card.status === "founder" && (
                      <div className="pk-th-pill pk-th-pill--founder">
                        <IconCrown size={9} />
                        Founding members only
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        {/* CREDIT STORE */}
        {activeTab === "store" && (
          <div role="tabpanel">
            <PerksCreditsHub
              userId={currentUser?.uid}
              isCreator={userProfile?.role === "creator" || userProfile?.isCreator}
              balance={credits}
              onRefresh={() => refreshUserPerks(currentUser?.uid)}
            />

            <div className="pk-sec-row" style={{ marginTop: 32 }}>
              <div>
                <div className="pk-sec-eyebrow">Spend</div>
                <h2 className="pk-sec-title">
                  Spend your <em>credits</em>
                </h2>
              </div>
            </div>

            <div className="pk-store-hero">
              <div>
                <div className="pk-store-bal-eyebrow">Your balance</div>
                <div className="pk-store-bal-num">{formatCount(credits)}</div>
                <div className="pk-store-bal-label">credits ready to spend</div>
              </div>
              <div className="pk-store-afford">
                <div className="pk-store-afford-label">You can afford</div>
                {affordItems.length === 0 ? (
                  <div className="pk-store-afford-item">Keep earning — you're getting close</div>
                ) : (
                  affordItems.map((label) => (
                    <div key={label} className="pk-store-afford-item">
                      {label.includes("Wall") ? (
                        <IconMessage size={13} color="rgba(255,255,255,0.5)" />
                      ) : label.includes("boost") ? (
                        <IconArrow size={13} color="rgba(255,255,255,0.5)" />
                      ) : (
                        <IconPalette size={13} color="rgba(255,255,255,0.5)" />
                      )}
                      {label}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="pk-store-sec-label">
              <IconHeart size={13} color="var(--pk-text-muted)" />
              Support creators
            </div>
            <div className="pk-st-grid">
              {creatorStoreItems.map((item) => {
                const canAfford = credits >= item.cost;
                return (
                  <article
                    key={item.id}
                    className={`pk-st-card${!canAfford ? " pk-st-card--dim" : ""}`}
                  >
                    <StoreIcon id={item.id} />
                    <h3>{STORE_LABELS[item.id] || item.name}</h3>
                    <p>{item.description}</p>
                    <button
                      type="button"
                      className={`pk-st-btn ${canAfford ? "pk-st-btn--can" : "pk-st-btn--cant"}`}
                      onClick={() => handlePurchase(item)}
                      disabled={loading || !canAfford}
                    >
                      {canAfford
                        ? `✦ ${formatCount(item.cost)} credits`
                        : `Need ${formatCount(item.cost - credits)} more credits`}
                    </button>
                  </article>
                );
              })}
            </div>

            <div className="pk-store-sec-label">
              <IconMessage size={13} color="var(--pk-text-muted)" />
              Appreciation Wall
            </div>
            <div className="pk-st-grid">
              {wallStoreItems.map((item) => {
                const canAfford = credits >= item.cost;
                return (
                  <article
                    key={item.id}
                    className={`pk-st-card${!canAfford ? " pk-st-card--dim" : ""}`}
                  >
                    <StoreIcon id={item.id} />
                    <h3>{STORE_LABELS[item.id] || item.name}</h3>
                    <p>{item.description}</p>
                    <button
                      type="button"
                      className={`pk-st-btn ${canAfford ? "pk-st-btn--can" : "pk-st-btn--cant"}`}
                      onClick={() => handlePurchase(item)}
                      disabled={loading || !canAfford}
                    >
                      {canAfford
                        ? `✦ ${formatCount(item.cost)} credits`
                        : `Need ${formatCount(item.cost - credits)} more credits`}
                    </button>
                  </article>
                );
              })}
            </div>
          </div>
        )}

        {/* MY ITEMS */}
        {activeTab === "owned" && (
          <div role="tabpanel">
            <div className="pk-sec-row">
              <div>
                <div className="pk-sec-eyebrow">History</div>
                <h2 className="pk-sec-title">
                  Things you&apos;ve <em>purchased</em>
                </h2>
              </div>
            </div>
            <PerksInventory purchases={purchases} onBrowseStore={() => setActiveTab("store")} />
          </div>
        )}
      </div>
    </div>
  );
}
